import { useQuery } from '@tanstack/react-query'
import {
  getPondComparisonSearchHistoryKey,
  getPondComparisonFoodKey,
  getPondComparisonFoodStandardKey,
  getPondComparisonGrowthKey,
  getPondComparisonGrowthStandardKey,
  getPondComparisonDOKey,
  getPondComparisonPHKey,
  getPondComparisonTableKey,
} from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { useEffect } from 'react'

// DOF-1187 fix: rapid add/remove of ponds while the user builds their
// selection was firing one `pond_comparison_run` per edit. Debounce so only
// the settled selection reports a run.
const COMPARISON_RUN_DEBOUNCE_MS = 1500

import {
  getSearchHistory,
  getFoodComparison,
  getFoodStandard,
  getGrowthComparison,
  getGrowthStandard,
  getDOComparison,
  getPHComparison,
  getTableComparison,
} from './index'

export const useGetSearchHistory = () => {
  return useQuery({
    queryKey: getPondComparisonSearchHistoryKey(),
    queryFn: async () => {
      const { data } = await getSearchHistory()
      return data.data ?? []
    },
    staleTime: Infinity,
    retry: false,
  })
}

export const useGetFoodComparison = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')
  return useQuery({
    queryKey: getPondComparisonFoodKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getFoodComparison({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetFoodStandard = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')

  return useQuery({
    queryKey: getPondComparisonFoodStandardKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getFoodStandard({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetGrowthComparison = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')

  return useQuery({
    queryKey: getPondComparisonGrowthKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getGrowthComparison({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetGrowthStandard = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')

  return useQuery({
    queryKey: getPondComparisonGrowthStandardKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getGrowthStandard({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetDOComparison = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')

  return useQuery({
    queryKey: getPondComparisonDOKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getDOComparison({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetPHComparison = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')

  return useQuery({
    queryKey: getPondComparisonPHKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getPHComparison({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })
}

export const useGetTableComparison = (cycleIds: string[]) => {
  const idsParam = cycleIds.join(',')
  const { trackEvent } = useAnalytics()

  const query = useQuery({
    queryKey: getPondComparisonTableKey(cycleIds),
    queryFn: async () => {
      if (!idsParam) return null
      const { data } = await getTableComparison({ id: idsParam })
      return data.data
    },
    enabled: cycleIds.length > 0,
    staleTime: Infinity,
  })

  const { data } = query

  // DOF-1187: the comparison TABLE is the literal comparison result. Tied to
  // the displayed `data` (not useQuery's `onSuccess`) because `onSuccess`
  // only fires on an actual network fetch in TanStack Query v4 — it's
  // skipped when `staleTime: Infinity` serves an already-cached result to a
  // re-mounted/re-keyed observer (e.g. the user re-selects the same ponds
  // within the cache window), which would otherwise silently undercount.
  // Debounced so rapid add/remove while building a selection settles into
  // one event instead of one per intermediate state.
  useEffect(() => {
    if (!data) return
    const timer = setTimeout(() => {
      trackEvent(AnalyticsEvents.pondComparisonRun, { pond_count: data.length })
    }, COMPARISON_RUN_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [idsParam, data, trackEvent])

  return query
}
